import { useState, useCallback } from 'react';
import {
  getRoleWithEntitlements,
  getFeatureWiseEntitlementList,
  updateRoleWithEntitlements,
} from 'Api/apiService';
import { SbNotification } from 'uiCore';
import { ERROR_MESSAGES, ERROR_TITLES, WARNING_TITLES, getApiErrorMessage } from 'constants/profileManagement.constants';
import {
  normalizeFeatureEntitlements,
  normalizeRoleEntitlements,
} from 'Utils/profileAccess.utils';

const useProfileDrawer = () => {
  const [featureEntitlements, setFeatureEntitlements] = useState(null);
  const [roleEntitlementData, setRoleEntitlementData] = useState(null);
  const [selectedEntitlements, setSelectedEntitlements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadEntitlements = useCallback(async ({ roleId, featureId }) => {
    setLoading(true);
    try {
      const [featureRes, roleRes] = await Promise.all([
        getFeatureWiseEntitlementList({ selectedFeatureId: featureId }),
        getRoleWithEntitlements({
          selectedRoleId: roleId,
          selectedFeatureId: featureId,
        }),
      ]);

      // Check for error responses in body (HTTP 200 with error field)
      if (featureRes?.error || featureRes?.error_code || roleRes?.error || roleRes?.error_code) {
        SbNotification.warning(WARNING_TITLES.FETCH_ENTITLEMENTS, ERROR_MESSAGES.FETCH_ENTITLEMENTS_FAILED, { key: 'profile-entitlement-warning' });
        setLoading(false);
        return;
      }

      // Normalize using same utils as old Redux actions
      const normalizedFeature = featureRes
        ? normalizeFeatureEntitlements(featureRes)
        : null;
      const normalizedRole = roleRes
        ? normalizeRoleEntitlements(roleRes)
        : null;

      setFeatureEntitlements(normalizedFeature);
      setRoleEntitlementData(normalizedRole);

      // Derive initially selected entitlements (same logic as old ProfilesAccessDrawer)
      if (normalizedFeature && normalizedRole) {
        const matched = normalizedFeature.groups
          .filter((group) => group.visible)
          .flatMap((group) => {
            const visibleIds = group.entitlements.map(
              (entitlement) => entitlement.entitlementId,
            );
            const isFullGroupSelected =
              visibleIds.length > 0 &&
              visibleIds.every((id) =>
                normalizedRole.entitlementIds.includes(id),
              );
            return isFullGroupSelected ? visibleIds : [];
          });
        setSelectedEntitlements(matched);
      } else {
        setSelectedEntitlements([]);
      }
    } catch (error) {
      const msg = getApiErrorMessage(error, ERROR_MESSAGES.FETCH_ENTITLEMENTS_FAILED);
      SbNotification.warning(WARNING_TITLES.FETCH_ENTITLEMENTS, msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleGroup = useCallback(
    (group) => {
      const groupEntitlementIds = group.entitlements.map(
        (e) => e.entitlementId,
      );
      const allSelected = groupEntitlementIds.every((id) =>
        selectedEntitlements.includes(id),
      );

      if (allSelected) {
        setSelectedEntitlements((prev) =>
          prev.filter((id) => !groupEntitlementIds.includes(id)),
        );
      } else {
        setSelectedEntitlements((prev) => [
          ...prev,
          ...groupEntitlementIds.filter((id) => !prev.includes(id)),
        ]);
      }
    },
    [selectedEntitlements],
  );

  const isGroupSelected = useCallback(
    (group) =>
      group.entitlements.length > 0 &&
      group.entitlements.every((e) =>
        selectedEntitlements.includes(e.entitlementId),
      ),
    [selectedEntitlements],
  );

  // Select all visible entitlements
  const selectAll = useCallback(() => {
    const visibleIds = (featureEntitlements?.groups || [])
      .filter((g) => g.visible)
      .flatMap((g) => g.entitlements.map((e) => e.entitlementId));
    setSelectedEntitlements(visibleIds);
  }, [featureEntitlements]);

  // Remove all visible entitlements
  const removeAll = useCallback(() => {
    const visibleIds = (featureEntitlements?.groups || [])
      .filter((g) => g.visible)
      .flatMap((g) => g.entitlements.map((e) => e.entitlementId));
    setSelectedEntitlements((prev) =>
      prev.filter((id) => !visibleIds.includes(id)),
    );
  }, [featureEntitlements]);

  const isAllSelected = useCallback(() => {
    const visibleIds = (featureEntitlements?.groups || [])
      .filter((g) => g.visible)
      .flatMap((g) => g.entitlements.map((e) => e.entitlementId));
    return visibleIds.length > 0 && visibleIds.every((id) => selectedEntitlements.includes(id));
  }, [featureEntitlements, selectedEntitlements]);

  const isNoneSelected = useCallback(() => {
    return selectedEntitlements.length === 0;
  }, [selectedEntitlements]);

  const saveEntitlements = useCallback(
    async ({ roleId, featureId, description, legalEntity, businessUnit, func, subFunction }) => {
      setSaving(true);
      try {
        // Collect hidden group entitlements (same as old drawer)
        const hiddenEntitlementIds = (featureEntitlements?.groups || [])
          .filter((g) => g.visible === false)
          .flatMap((g) => g.entitlements.map((e) => e.entitlementId));

        const finalEntitlementIds = Array.from(
          new Set([...selectedEntitlements, ...hiddenEntitlementIds]),
        );

        const payload = {
          description: description || '',
          legal_entity: (legalEntity || []).join(','),
          business_unit: (businessUnit || []).join(','),
          function: (func || []).join(','),
          sub_function: (subFunction || []).join(','),
          entitlement_ids: finalEntitlementIds,
        };

        await updateRoleWithEntitlements({
          selectedRoleId: roleId,
          selectedFeatureId: featureId,
          payload,
        });

        SbNotification.success('Success', 'Entitlements updated successfully');
        return true;
      } catch (error) {
        const msg = getApiErrorMessage(error, ERROR_MESSAGES.UPDATE_ENTITLEMENTS_FAILED);
        SbNotification.error(ERROR_TITLES.UPDATE_ENTITLEMENTS, msg);
        return false;
      } finally {
        setSaving(false);
      }
    },
    [selectedEntitlements, featureEntitlements],
  );

  const sortedGroups = (featureEntitlements?.groups || [])
    .filter((g) => g.visible !== false)
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

  return {
    featureEntitlements,
    roleEntitlementData,
    selectedEntitlements,
    setSelectedEntitlements,
    sortedGroups,
    loading,
    saving,
    loadEntitlements,
    toggleGroup,
    isGroupSelected,
    selectAll,
    removeAll,
    isAllSelected,
    isNoneSelected,
    saveEntitlements,
  };
};

export default useProfileDrawer;
